import type { CameraId, IncidentId, SiteId, SspId, SsuId } from '@cguard/types';

const edgeApplianceTwinKeys = Object.assign(() => ['/v1/edge/appliances/twins'] as const, {
  summary: () => ['/v1/edge/appliances/twins', 'summary'] as const,
});

const edgeApplianceInventoryKeys = Object.assign(() => ['/v1/edge/appliances'] as const, {
  summary: () => ['/v1/edge/appliances', 'stock-summary'] as const,
});

/**
 * Cross-endpoint invalidation keys.
 *
 * Orval generates a per-endpoint key factory (`getListIncidentsQueryKey`,
 * `getGetIncidentQueryKey`, etc.) — those are the canonical keys used by the
 * generated hooks. This file exists only for **broad** invalidation patterns:
 * when an `acknowledgeIncident` mutation succeeds we want to invalidate the
 * entire incidents tree, not just the exact `getListIncidentsQueryKey()` we
 * happen to know about.
 *
 * Pattern in app code:
 *   queryClient.invalidateQueries({ queryKey: queryKeys.incidents() })
 *
 * Note: orval's generated keys start with the request URL string (e.g.
 * `/v1/incidents`), so cross-cutting invalidation just matches the URL
 * prefix via `queryKey: ['/v1/incidents']` and TanStack Query's default
 * fuzzy matching does the right thing. The `/v1` prefix mirrors the real
 * `MapGroup` layout in `services/central/.../*ModuleEndpoints.cs`; when the
 * frontend was MSW-only the stub used `/api/...`, but production builds now
 * hit the real central API at `/v1/...`.
 */
export const queryKeys = {
  incidents: () => ['/v1/incidents'] as const,
  incidentDetail: (id: IncidentId) => ['/v1/incidents', id] as const,
  cameras: () => ['/v1/cameras'] as const,
  cameraDetail: (id: CameraId) => ['/v1/cameras', id] as const,
  sites: () => ['/v1/sites'] as const,
  siteDetail: (id: SiteId) => ['/v1/sites', id] as const,
  health: () => ['/v1/health/system'] as const,
  edgeApplianceTwins: edgeApplianceTwinKeys,
  edgeApplianceInventory: edgeApplianceInventoryKeys,
  edgeApplianceTwinDetail: (applianceId: string) =>
    ['/v1/edge/appliances/twins', applianceId] as const,
  remoteAccessSessions: (applianceId: string) =>
    ['/v1/edge/appliances', applianceId, 'remote-access-sessions'] as const,
  edgeRollouts: () => ['/v1/edge/rollouts'] as const,
  siteCommissioning: (siteId: SiteId | string) => ['/v1/edge/commissioning', siteId] as const,
  recorderQuarantines: () => ['/v1/edge/commissioning', 'recorder-quarantines'] as const,
} as const;

export interface IncidentListFilters {
  state?: ReadonlyArray<'open' | 'acknowledged' | 'dispatched' | 'closed'>;
  tiers?: ReadonlyArray<1 | 2 | 3 | 4 | 5>;
  sspId?: SspId;
  ssuId?: SsuId;
}
